import { useTranslation } from "react-i18next";

import { formatImgUrl } from "../../../utils/cardFormating";
import {
  getAgentProfileRoute,
  getCoachProfileRoute,
  getEntityProfileRoute,
  getOtherProfileRoute,
  getPlayerProfileRoute,
  getScoutProfileRoute,
} from "../../../routers/routeUtils";
import { PlayerPositionsShortEnum } from "../enums/playerPositions";
import { User } from "../../../types/types";
import { ServiceProvider } from "../../GoService/api/types/topServiceProviders.types";
import { useListQuery } from "../../../api/queries/lists";

interface FormattedUserData {
  fullName: string;
  firstTwoChr: string;
  about: string;
  club: string;
  countryName: string;
  countryMedia: string;
  nationality: string;
  cityName: string;
  media: string | undefined;
  mediaFileName: string;
  role: string;
  formattedInfo: (separator: string) => string;
  profileViewers: number;
  verificationStatus: string;
  isVerified: boolean;
  posts: number;
  followersCount: number;
  followingCount: number;
  id: number | null;
  entityId: number | null;
  isFollowing: boolean;
  profileLink: string;
  mutualFollowersCount: number;
  countryFlag: string;
  playerPositionShort: string | null;
  friendsCount: number | null;
  isManagedByEntity: boolean;
  entityLevel: string | null;
  mutualFriendsCount: number | null;
  additionalProperties: { label: string; value: string | number }[];
}

const DEFAULT_VALUES: FormattedUserData = {
  fullName: "",
  firstTwoChr: "",
  about: "",
  club: "",
  countryName: "",
  countryMedia: "",
  nationality: "",
  cityName: "",
  media: undefined,
  mediaFileName: "",
  role: "",
  formattedInfo: () => "",
  profileViewers: 0,
  verificationStatus: "",
  isVerified: false,
  posts: 0,
  followersCount: 0,
  followingCount: 0,
  id: null,
  entityId: null,
  isFollowing: false,
  profileLink: "",
  mutualFollowersCount: 0,
  countryFlag: "",
  playerPositionShort: null,
  friendsCount: null,
  isManagedByEntity: false,
  entityLevel: null,
  mutualFriendsCount: null,
  additionalProperties: [],
};

// To be moved to the main router utilities
const getProfileLinkByRole = (user: User | ServiceProvider): string => {
  switch (user.role) {
    case "PLAYER":
      return getPlayerProfileRoute(user.id);
    case "AGENT":
      return getAgentProfileRoute(user.id);
    case "SCOUT":
      return getScoutProfileRoute(user.id);
    case "COACH":
      return getCoachProfileRoute(user.id);
    default:
      return getOtherProfileRoute(user.id);
  }
};

export const useUserDataFormatter = (data: User | ServiceProvider | undefined) => {
  const { t } = useTranslation("common");
  const { t: tUser } = useTranslation("user", { keyPrefix: "userAdditionalProperties" });

  const { data: formationList, isLoading: isLoadingFormationList } = useListQuery("formation");
  if (!data) {
    return DEFAULT_VALUES;
  }

  const getTranslatedEntityLevel = (level: string): string | null => {
    const translated = t(`entity.entityLevel.${level}`);
    return translated === `entity.entityLevel.${level}` ? null : translated;
  };

  const formatLocationInfo = (role: string, city: string, country: string, club: string, separator: string): string => {
    const parts: string[] = [role];

    if (club) {
      parts.push(club);
    }
    if (city) {
      parts.push(city);
    }
    if (country) {
      parts.push(country);
    }

    return parts.join(separator ? `${separator} ` : " • ");
  };

  const formatEntityData = (entity: NonNullable<User["entity"]>): FormattedUserData => {
    const translatedRole = t(`entity.entityType.${entity.type}`);
    const countryName = entity.country?.name || "";
    const nationality = entity.country?.name || "";
    return {
      fullName: entity.name,
      firstTwoChr: entity.name.trim().slice(0, 2).toLocaleUpperCase(),
      about: entity.about,
      club: "",
      countryName,
      countryMedia: formatImgUrl(entity.country?.url || "", true),
      nationality,
      cityName: "",
      media: formatImgUrl(entity.mediaUrl),
      mediaFileName: entity.mediaUrl,
      role: translatedRole,
      formattedInfo: (separator: string) => formatLocationInfo(translatedRole, "", countryName, "", separator),
      profileViewers: entity.profileViewers || 0,
      verificationStatus: entity.status || "",
      isVerified: entity.status === "approved",
      posts: entity.posts || 0,
      followersCount: entity.followersCount,
      followingCount: entity.followingCount || 0,
      id: entity.userId,
      entityId: entity.id,
      isFollowing: entity.isFollowing || false,
      profileLink: getEntityProfileRoute(entity.id),
      mutualFollowersCount: 0,
      countryFlag: formatImgUrl(entity.country?.url || ""),
      playerPositionShort: null,
      friendsCount: null,
      isManagedByEntity: entity.isManagedByEntity,
      entityLevel: getTranslatedEntityLevel(entity.level),
      mutualFriendsCount: null,
      additionalProperties: [],
    };
  };

  const formatAdditionalProperties = (user: User | ServiceProvider): { label: string; value: string | number }[] => {
    if (!user.additionalProperties) return [];

    const properties: { label: string; value: string | number }[] = [];
    const { additionalProperties } = user;

    switch (user.role) {
      case "PLAYER":
        if (additionalProperties.height)
          properties.push({ label: tUser("height"), value: additionalProperties.height });
        if (additionalProperties.weight)
          properties.push({ label: tUser("weight"), value: additionalProperties.weight });
        if (additionalProperties.strongFoot)
          properties.push({ label: tUser("strongFoot"), value: additionalProperties.strongFoot });
        if (additionalProperties.primaryPosition)
          properties.push({
            label: tUser("primaryPosition"),
            value:
              PlayerPositionsShortEnum[additionalProperties.primaryPosition as keyof typeof PlayerPositionsShortEnum] ??
              null,
          });
        if (additionalProperties.otherPosition)
          properties.push({
            label: tUser("otherPosition"),
            value:
              PlayerPositionsShortEnum[additionalProperties.otherPosition as keyof typeof PlayerPositionsShortEnum] ??
              null,
          });
        break;
      case "COACH":
        if (additionalProperties.preferredFormation) {
          const formationNames = additionalProperties.preferredFormation.map(
            (id) => formationList?.find((entry) => entry.id === id)?.name
          );
          properties.push({
            label: tUser("preferredFormation"),
            value: formationNames.join("-"),
          });
        }
        if (additionalProperties.totalTeamsCoached)
          properties.push({
            label: tUser("totalTeamsCoached"),
            value: additionalProperties.totalTeamsCoached.length,
          });
        break;
      case "AGENT":
        if (additionalProperties.totalRepresented)
          properties.push({
            label: tUser("totalRepresented"),
            value: additionalProperties.totalRepresented,
          });
        if (additionalProperties.totalCareerTransfers)
          properties.push({
            label: tUser("totalCareerTransfers"),
            value: additionalProperties.totalCareerTransfers,
          });
        break;
      case "SCOUT":
        if (additionalProperties.totalScoutedPlayers)
          properties.push({
            label: tUser("totalScoutedPlayers"),
            value: additionalProperties.totalScoutedPlayers,
          });
        if (additionalProperties.typeOfScouting)
          properties.push({
            label: tUser("typeOfScouting"),
            value: additionalProperties.typeOfScouting,
          });
        break;
    }

    return properties;
  };

  const formatUserData = (user: User | ServiceProvider): FormattedUserData => {
    if (user.entity) {
      return formatEntityData(user.entity);
    }

    const primaryPosition = user.additionalProperties?.primaryPosition;
    const positionShort = PlayerPositionsShortEnum[primaryPosition as keyof typeof PlayerPositionsShortEnum] || null;
    let translatedRole = "";
    if (user.role === "OTHER" && user.subRole) {
      translatedRole = t(`user.role.${user.subRole}`);
    } else if (user.role) {
      translatedRole = t(`user.role.${user.role}`);
    }
    const countryName = user.country?.name || "";
    const cityName = user.city || "";
    const club = "team" in user ? user.team?.name || "" : "";
    const nationality = user.nationality?.name || "";
    return {
      fullName: `${user.firstName} ${user.lastName}`,
      firstTwoChr: `${user.firstName?.trim().slice(0, 1)}${user.lastName?.trim().slice(0, 1)}`.toLocaleUpperCase(),
      about: user.bio,
      club,
      countryName,
      nationality,
      countryMedia: formatImgUrl(user.country?.url || "", true),
      cityName,
      media: user.media?.url ? formatImgUrl(user.media.url) : "",
      mediaFileName: user.media?.url || "",
      role: translatedRole,
      formattedInfo: (separator: string) => formatLocationInfo(translatedRole, cityName, countryName, club, separator),
      profileViewers: 0,
      verificationStatus: user.verificationStatus || "",
      isVerified: user.verificationStatus === "VERIFIED",
      posts: 0,
      followersCount: user.followersCount,
      followingCount: 0,
      id: user.id,
      entityId: null,
      isFollowing: user.isFollowing || false,
      profileLink: getProfileLinkByRole(user),
      mutualFollowersCount: 0,
      countryFlag: formatImgUrl(user.country?.url || ""),
      playerPositionShort: positionShort,
      friendsCount: user.friendsCount > 0 ? user.friendsCount : null,
      mutualFriendsCount: user?.mutualFriendsCount || null,
      isManagedByEntity: false,
      entityLevel: null,
      additionalProperties: formatAdditionalProperties(user),
    };
  };

  return formatUserData(data);
};
